import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, UserPlus, UserCircle, Users, CirclePlus, X } from 'lucide-react';
import { getLayout, getDashboard, getRecipients, saveRecipients, downloadEmailDraft, getEmailPreview } from '../../services/reportingHubApi';
import { decodedToken } from '../../utils/auth';
import permissionConfig from '../../utils/permission.json';
import { renderWidget } from './reportWidgets';

// Widget renderers are centralized in ./reportWidgets (renderWidget).

// Section titles shown above each block (reference style).
const SECTION_TITLE = {
  highlight: 'Highlight issues',
  table: 'Table',
  chart: 'Chart',
};

function WidgetCard({ widget, dashboard, plain }) {
  const body = renderWidget(widget.widgetKey, { product: 'Hardware', data: dashboard, widgetName: widget.widgetName });

  if (plain) {
    return (
      <div className="w-full overflow-x-auto">
        {body}
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-hidden bg-white p-2 sm:p-3 md:p-4 border border-gray-300">
      <div className="w-full overflow-x-auto">
        {body}
      </div>
    </div>
  );
}

function SectionView({ section, dashboard }) {
  if (
    section.sectionKey === "header" ||
    section.widgets.length === 0
  )
    return null;

  const full = section.widgets.filter(
    (w) => w.side === "full"
  );

  const sided = section.widgets
    .filter((w) => w.side !== "full")
    .sort((a, b) =>
      a.side === "left" ? -1 : 1
    );

  const title =
    SECTION_TITLE[section.sectionKey] ||
    section.sectionName;

  return (
    <div className="sm:mb-8 mb-2 border-b border-gray-400">
      <h2 className="text-base font-bold mb-1 pb-1">
        {title}
      </h2>

      {/* Only widgets get padding */}
      <div className="pt-3 sm:pb-5 pb-2 px-2 sm:px-4 md:px-6 lg:px-8 xl:px-10">
        {full.map((w) => (
          <div
            key={w.widgetKey}
            className="mb-4"
          >
            <WidgetCard
              widget={w}
              dashboard={dashboard}
              plain={
                w.widgetKey ===
                "highlight_issues"
              }
            />
          </div>
        ))}

        {sided.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {sided.map((w) => (
              <WidgetCard
                key={w.widgetKey}
                widget={w}
                dashboard={dashboard}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const HardwareReportView = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [template, setTemplate] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [error, setError] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ to: '', cc: '', bcc: '', subject: '' });
  const [savedForm, setSavedForm] = useState(null);
  const [modalErr, setModalErr] = useState('');
  const [modalBusy, setModalBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const bodyRef = useRef(null);
  const location = useLocation();

  const user = decodedToken() || {};
  const userName = user.userId || user.email || 'User';
  const canSendEmail = permissionConfig.SendEmailHardware?.[user.userType] === true;
  const syncTime = new Date().toLocaleString('en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).replace(',', '');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [layoutRes, dash] = await Promise.all([
          getLayout(),
          getDashboard(projectId).catch(() => null),
        ]);
        if (!active) return;
        setTemplate(layoutRes.template);
        setDashboard(dash);
      } catch (e) {
        if (active) {
          setError(
            e.response?.status === 404
              ? 'Default Hardware template not found. Run the seed script first.'
              : 'Failed to load layout template.'
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [projectId]);

  const handleEmail = async () => {
    setEmailBusy(true);
    setEmailMsg('');
    try {
      const data = await getEmailPreview(projectId);
      setPreview(data); // open preview modal
      setEditSubject(data.subject || '');
    } catch (e) {
      if (e.response?.status === 409) {
        setShowWarning(true); // no recipients configured
      } else {
        setEmailMsg('Unable to build email preview.');
      }
    } finally {
      setEmailBusy(false);
    }
  };

  const handleDownload = async () => {
    setDownloadBusy(true);
    try {
      const bodyHtml = bodyRef.current ? bodyRef.current.innerHTML : preview.bodyHtml;
      const res = await downloadEmailDraft(projectId, { bodyHtml, subject: editSubject });
      setEmailMsg(`Draft downloaded: ${res.fileName}`);
      setPreview(null);
    } catch {
      setEmailMsg('Unable to download draft.');
    } finally {
      setDownloadBusy(false);
    }
  };

  // Open the Edit Recipient modal, prefilling from saved recipients (or header).
  const openRecipientModal = async () => {
    setModalErr('');
    setShowWarning(false);
    let initial = { to: '', cc: '', bcc: '', subject: header };
    try {
      const data = await getRecipients(projectId);
      if (data?.recipients) {
        const r = data.recipients;
        initial = { to: r.to || '', cc: r.cc || '', bcc: r.bcc || '', subject: r.subject || header };
      }
    } catch {
      // 404 => no saved list yet; keep defaults
    }
    setForm(initial);
    setSavedForm(initial);
    setShowModal(true);
  };

  const saveRecipientList = async () => {
    if (!form.to.trim()) {
      setModalErr('The "To" field must include at least one recipient.');
      return;
    }
    setModalBusy(true);
    setModalErr('');
    try {
      await saveRecipients(projectId, form);
      setSavedForm(form);
      setShowModal(false);
      setEmailMsg('Recipient list saved.');
    } catch (e) {
      setModalErr(e.response?.data?.error || 'Failed to save recipients.');
    } finally {
      setModalBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="sm:w-15 sm:h-15 w-6 h-6 animate-spin text-blue-800" />
      </div>
    );
  }

  const header = dashboard?.header || (template ? `${template.subCategory} Report` : 'Hardware Report');

  return (
    // <div className="min-h-screen bg-black text-white sm:p-6 p-4">
    //   <div className="w-full mx-auto">
    <div className="min-h-screen bg-black text-white p-4 sm:p-6 overflow-x-hidden">
      <div className="w-full max-w-full mx-auto">
        {/* <div className="flex items-center justify-between mb-6 gap-4 flex-wrap border-b border-gray-700 pb-4">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-800 shrink-0" aria-label="Go back">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl md:text-2xl font-bold break-all">{header}</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={handleEmail}
                disabled={emailBusy}
                className="flex items-center gap-2 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50 transition-colors text-sm font-medium"
              >
                {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Send Email
              </button>
              <button
                onClick={openRecipientModal}
                title="Edit recipient"
                className="p-2 rounded hover:bg-gray-800 text-gray-300"
              >
                <UserPlus className="w-5 h-5" />
              </button>
            </div>
            <div className="text-right text-xs text-gray-400 leading-tight hidden md:block">
              <div>Last successful sync time:</div>
              <div className="text-gray-300">{syncTime}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <UserCircle className="w-7 h-7 text-gray-400" />
              <span className="text-sm text-gray-200 max-w-[120px] truncate">{userName}</span>
            </div>
          </div>
        </div> */}

        <div className="border-b border-gray-400 pb-4 mb-4 sm:mb-6 mt-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 -ml-2">

            {/* LEFT SECTION */}
            <div className="flex items-center gap-1 min-w-0 sm:-ml-4 -ml-2">
              <button
                // onClick={() => navigate(-1)}
                onClick={() =>
                  navigate('/reporting-hub/hardware', {
                    state: location.state,
                  })
                }
                className="p-2 rounded hover:bg-gray-800 shrink-0"
                aria-label="Go back"
              >
                <ArrowLeft className="w-6 h-6 sm:w-6 sm:h-6 font-bold text-white font-bold" />
              </button>

              <h1 className="font-bold text-sm sm:text-xl md:text-2xl lg:text-2xl">
                {header}
              </h1>
            </div>

            {/* RIGHT CONTENT */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between lg:justify-end gap-4 sm:gap-6">

              {/* EMAIL ACTIONS */}
              <div className="flex items-center gap-3">

                {canSendEmail && (
                <button
                  onClick={handleEmail}
                  disabled={emailBusy}
                  className="flex items-center gap-2 px-3 py-2 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50 transition-colors text-xs sm:text-sm font-medium whitespace-nowrap"
                >
                  {emailBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  <span>Send Email</span>
                </button>
                )}

                {/* Recipient Button */}
                {canSendEmail && (
                <div className="relative group">
                  <button
                    onClick={openRecipientModal}
                    className="p-2 rounded hover:bg-gray-800 text-gray-300 flex items-center gap-1"
                  >
                    <Users className="w-5 h-5" />
                    <CirclePlus className="w-5 h-5 text-yellow-500" />
                  </button>

                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                    <div className="relative px-2 py-1 text-xs text-white bg-black border border-gray-500 rounded-md whitespace-nowrap shadow-lg">
                      Edit recipient

                      <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-gray-500" />
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-[-1px] w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-black" />
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* USER + SYNC */}
              {/* <div className="flex flex-col items-start sm:items-end "> */}
              <div className="flex flex-col items-start sm:items-end -mt-1 sm:-mt-5">
                <div className="text-[13px] sm:text-xs text-white text-left sm:text-right leading-tight">
                  <div>Last successful sync time:</div>
                  <div className="text-white">
                    {syncTime}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <UserCircle className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400" />
                  <span className="text-xs sm:text-sm text-white max-w-[140px]">
                    {userName}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>

        {(emailMsg) && (
          <div className="mb-4 text-xs text-gray-400">
            <span className="text-gray-300">{emailMsg}</span>
          </div>
        )}

        {error && (
          <div className="bg-red-900/40 border border-red-700 text-red-200  px-4 py-3 mb-4">{error}</div>
        )}

        {template?.sections
          ?.slice()
          .sort((a, b) => a.position - b.position)
          .map((section) => (
            <SectionView key={section.sectionKey} section={section} dashboard={dashboard} />
          ))}

        {/* Email preview modal (with Download button) */}
        {preview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4">
            <div
              className="
        bg-white
        text-black
        w-full
        max-w-full
        max-h-[100vh]
        flex
        flex-col
        overflow-hidden
      "
            >
              {/* Header */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-black">
                <h3 className="text-base sm:text-lg md:text-2xl font-bold text-white">
                  Download Draft Email — Preview (.eml file)
                </h3>

                <button
                  onClick={() => setPreview(null)}
                  className="
            text-gray-500
            hover:text-black
            p-1
            
          "
                >
                  <X className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="px-3 sm:px-5 py-3 overflow-y-auto flex-1">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm mb-4 min-w-[600px]">
                    <tbody>
                      {[
                        ['To', preview.to],
                        ['Cc', preview.cc],
                        ['Bcc', preview.bcc],
                      ].map(([label, val]) => (
                        <tr key={label} className="align-top">
                          <td className="py-2 pr-3 font-semibold text-gray-600 w-20 whitespace-nowrap">
                            {label}
                          </td>

                          <td className="py-2">
                            <div className="border border-gray-300 px-2 py-2 bg-gray-50 break-words">
                              {val || (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}

                      <tr className="align-top">
                        <td className="py-2 pr-3 font-semibold text-gray-600 w-20 whitespace-nowrap">
                          Subject
                        </td>

                        <td className="py-2">
                          <input
                            value={editSubject}
                            onChange={(e) =>
                              setEditSubject(e.target.value)
                            }
                            className="
                      w-full
                      border border-gray-300
                      px-3 py-2
                      outline-none
                      focus:ring-2
                      focus:ring-blue-500
                    "
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Body Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                  <span className="text-sm font-semibold text-gray-600">
                    Email body
                  </span>

                  <span className="text-xs text-gray-400 font-bold">
                    Editable — click in the body to change text.
                    Wide tables/charts scroll sideways.
                  </span>
                </div>

                {/* Editable Body */}
                <div
                  ref={bodyRef}
                  contentEditable
                  suppressContentEditableWarning
                  className="
            border border-gray-300 
            p-3
            bg-white
            outline-none
            focus:ring-2
            focus:ring-blue-400
            overflow-x-auto
          "
                  style={{ minHeight: '250px' }}
                  dangerouslySetInnerHTML={{
                    __html: preview.bodyHtml,
                  }}
                />
              </div>

              {/* Footer Buttons */}
              <div
                className="
          border-t border-gray-200
          px-2 sm:px-2
          py-5 
          bg-black
        "
              >
                <div
                  className="
            flex
            flex-col
            sm:flex-row
            sm:justify-center
            gap-2
            sm:gap-30
            bg-black
          
          "
                >
                  <button
                    onClick={() => {
                      setPreview(null);
                      openRecipientModal();
                    }}
                    className="
              w-full
              sm:w-64
              px-4 py-2
              bg-gray-200
              hover:bg-gray-300
              text-black 
              font-semibold
              
            "
                  >
                    Edit Recipient
                  </button>

                  <button
                    onClick={() => setPreview(null)}
                    className="
              w-full
              sm:w-64
              px-4 py-2
              bg-gray-200
              hover:bg-gray-300
              text-black
              font-semibold
        
            "
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleDownload}
                    disabled={downloadBusy}
                    className="
              w-full
              sm:w-64
              px-4 py-2
              bg-blue-600
              hover:bg-blue-500
              text-white
              disabled:opacity-50
              font-semibold
            
            "
                  >
                    {downloadBusy
                      ? 'Preparing...'
                      : 'Download Draft (.eml)'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Warning: no recipients configured */}
        {showWarning && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-gray-900 border border-gray-700 p-6 max-w-md w-full mx-4 text-center">
              <h3 className="text-lg font-semibold mb-3">Missing recipients</h3>
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-gray-300 mb-6">
                No default recipient list found. Please set up recipients first by clicking Edit recipient.
              </p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setShowWarning(false)} className="px-4 py-2 bg-gray-600 hover:bg-gray-500">
                  Cancel
                </button>
                <button onClick={openRecipientModal} className="px-4 py-2 bg-blue-600 hover:bg-blue-500">
                  Go to Edit recipient
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Email Recipient modal */}

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4">
            <div
              className="
    w-full
    max-h-[100vh]
    overflow-y-auto
    bg-gray-900
    border border-gray-700
    p-4 sm:p-6
  "
            >
              {/* Header */}
              <div className="flex items-start justify-between border-b border-gray-500 pb-3 mb-4">
                <div className="pr-3">
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white">
                    Edit Email Recipient
                  </h3>

                  <p className="text-sm sm:text-base md:text-lg text-white font-semibold mt-1">
                    Project: {header}
                  </p>
                </div>

                <button
                  onClick={() => setShowModal(false)}
                  className="
            text-gray-300
            hover:text-white
            hover:bg-gray-800
            p-1.5
            rounded
            transition-colors
            flex-shrink-0
          "
                  aria-label="Close"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Instructions */}
              <ul className="text-xs sm:text-sm md:text-base text-gray-300 list-disc pl-5 space-y-1 mb-4">
                <li>
                  Supports Outlook-style input. Paste multiple recipients using
                  semicolon (;).
                </li>
                <li>
                  "To" field must include at least one recipient.
                </li>
                <li>
                  CC and BCC are optional.
                </li>
                <li>
                  Subject is required.
                </li>
              </ul>


              <div className="px-0 sm:px-2 md:px-8 lg:px-10 xl:px-10 sm:-ml-5">
                {[
                  {
                    key: 'to',
                    label: 'To',
                    rows: 4,
                    ph: 'Name <a@x.com>; Other <b@y.com>',
                  },
                  {
                    key: 'cc',
                    label: 'CC',
                    rows: 2,
                    ph: 'optional',
                  },
                  {
                    key: 'bcc',
                    label: 'BCC',
                    rows: 2,
                    ph: 'optional',
                  },
                ].map((f) => (
                  <div
                    key={f.key}
                    className="
        mb-6
        flex
        flex-col
        md:flex-row
        md:items-center
        md:gap-6
      "
                  >
                    <label
                      className="
          w-full
          md:w-20
          lg:w-24
          text-sm
          sm:text-base
          font-medium
          text-white
          mb-2
          md:mb-0
          md:text-right
          flex-shrink-0
        "
                    >
                      {f.label} :
                      {f.key === 'to' && (
                        <span className="text-red-400"> *</span>
                      )}
                    </label>

                    <textarea
                      rows={f.rows}
                      value={form[f.key]}
                      placeholder={f.ph}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          [f.key]: e.target.value,
                        })
                      }
                      className="
          flex-1
          w-full
          bg-gray-800
          border border-gray-700
          px-3 py-2
          text-sm sm:text-base
          text-white
          outline-none
          focus:ring-2
          focus:ring-blue-500
        "
                    />
                  </div>
                ))}

                {/* Subject */}
                <div
                  className="
      mb-6
      flex
      flex-col
      md:flex-row
      md:items-center
      md:gap-6
    "
                >
                  <label
                    className="
        w-full
        md:w-20
        lg:w-24
        text-sm
        sm:text-base
        font-medium
        text-white
        mb-2
        md:mb-0
        md:text-right
        flex-shrink-0
      "
                  >
                    Subject :
                    <span className="text-red-400"> *</span>
                  </label>

                  <input
                    value={form.subject}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        subject: e.target.value,
                      })
                    }
                    className="
        flex-1
        w-full
        bg-gray-800
        border border-gray-700
        px-3 py-2
        text-sm sm:text-base
        text-white
        outline-none
        focus:ring-2
        focus:ring-blue-500
      "
                  />
                </div>
              </div>


              {/* Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-center gap-2 sm:gap-25 mt-5">
                <button
                  onClick={saveRecipientList}
                  disabled={modalBusy}
                  className="
      w-full sm:w-50
      px-4 py-2
      bg-blue-600
      hover:bg-blue-500
      disabled:opacity-50
      text-sm sm:text-base
      rounded-md
      text-center
    "
                >
                  {modalBusy ? 'Saving...' : 'Save as project default'}
                </button>

                <button
                  onClick={() => savedForm && setForm(savedForm)}
                  className="
      w-full sm:w-50
      px-4 py-2
      bg-gray-700
      hover:bg-gray-600
      text-sm sm:text-base
      rounded-md
      text-center
    "
                >
                  Reset to last saved
                </button>

                <button
                  onClick={() => setShowModal(false)}
                  className="
      w-full sm:w-50
      px-4 py-2
      bg-gray-700
      hover:bg-gray-600
      text-sm sm:text-base
      rounded-md
      text-center
    "
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default HardwareReportView;
